"use client";

import { useEffect, useRef, useState } from "react";

import { cn } from "@/lib/utils";

const DESKTOP_QUERY = "(min-width: 768px)";

export function HomeHeroVideo({ src, poster }: { src: string; poster: string }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [ready, setReady] = useState(false);
  

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    let cancelled = false;
    let posterImage: HTMLImageElement | null = null;

    const showFirstFrame = () => {
      if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
        setReady(true);
      }
    };

    const startVideo = () => {
      if (cancelled || !window.matchMedia(DESKTOP_QUERY).matches) return;
      video.muted = true;
      video.autoplay = true;
      video.preload = "auto";
      if (video.getAttribute("src") !== src) {
        video.src = src;
      }
      showFirstFrame();
      const playAttempt = video.play();
      if (playAttempt) playAttempt.catch(() => {});
    };

    const stopVideo = () => {
      setReady(false);
      video.removeAttribute("src");
      video.removeAttribute("poster");
      video.preload = "none";
      video.pause();
      video.load();
    };

    const enableDesktopVideo = () => {
      if (!window.matchMedia(DESKTOP_QUERY).matches) {
        stopVideo();
        return;
      }

      // Poster first: do not attach the webm until the still image has loaded.
      posterImage = new Image();
      posterImage.onload = startVideo;
      posterImage.onerror = startVideo;
      posterImage.src = poster;
      if (posterImage.complete) startVideo();
    };

    enableDesktopVideo();
    const media = window.matchMedia(DESKTOP_QUERY);
    media.addEventListener("change", enableDesktopVideo);
    return () => {
      cancelled = true;
      media.removeEventListener("change", enableDesktopVideo);
    };
  }, [poster, src]);

  return (
    <>
      <style
        dangerouslySetInnerHTML={{
          __html: `@media (min-width:768px){#home-hero-video-frame{background-image:url(${JSON.stringify(poster)})}}`,
        }}
      />
      <div
        id="home-hero-video-frame"
        className="absolute inset-0 z-0 hidden bg-black bg-cover bg-center md:block"
      >
        <video
          ref={videoRef}
          id="home-hero-video"
          aria-label="Pickcel digital signage running inside a retail store"
          width={619}
          height={402}
          loop
          muted
          playsInline
          preload="none"
          suppressHydrationWarning
          onPlaying={() => setReady(true)}
          onCanPlay={event => {
            if (event.currentTarget.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
              setReady(true);
            }
          }}
          className={cn(
            "relative z-0 h-full w-full object-cover transition-opacity duration-300",
            ready ? "opacity-100" : "opacity-0",
          )}
        />
      </div>
    </>
  );
}
